// frontend/src/components/shared/status-icon.ts
//
// The one table behind every filled status icon (plan 03 section 4.2): a solid
// circle with a white glyph, always shown with a word beside it. Pure data so
// the Explorer, the workbench and the tests read the same fills and words.
// The colours are written as hex values here, not palette tokens, so the icon
// is blue and green whatever the token values are (there is no gold).

export type StatusIconKind =
  | 'done'
  | 'needs_you'
  | 'working'
  | 'waiting'
  | 'gap'
  | 'not_applicable'
  | 'sent_back'
  | 'locked'

export type StatusIconSize = 12 | 14 | 16 | 18 | 20 | 34

export type StatusGlyph = 'tick' | 'bang' | 'dot' | 'clock' | 'cross' | 'dash' | 'return' | 'padlock'

export interface StatusIconSpec {
  /** Solid fill of the circle. */
  fill: string
  glyph: StatusGlyph
  /** The default word beside the icon and its accessible name. */
  word: string
  /** Tailwind class for the solid fill (literal, so the scanner keeps it). */
  fillClass: string
  /** Tailwind class for text in the same colour, for the word beside it. */
  textClass: string
}

export const STATUS_ICON: Record<StatusIconKind, StatusIconSpec> = {
  done: { fill: '#0B734D', glyph: 'tick', word: 'Done', fillClass: 'bg-[#0B734D]', textClass: 'text-[#0B734D]' },
  needs_you: { fill: '#1F4FB5', glyph: 'bang', word: 'Needs your decision', fillClass: 'bg-[#1F4FB5]', textClass: 'text-[#1F4FB5]' },
  working: { fill: '#4C6FD1', glyph: 'dot', word: 'Working', fillClass: 'bg-[#4C6FD1]', textClass: 'text-[#4C6FD1]' },
  waiting: { fill: '#7F89A8', glyph: 'clock', word: 'Waiting', fillClass: 'bg-[#7F89A8]', textClass: 'text-[#5B6584]' },
  gap: { fill: '#B4380B', glyph: 'cross', word: 'Gap', fillClass: 'bg-[#B4380B]', textClass: 'text-[#B4380B]' },
  not_applicable: {
    fill: '#7F89A8',
    glyph: 'dash',
    word: 'Not applicable',
    fillClass: 'bg-[#7F89A8]',
    textClass: 'text-[#5B6584]',
  },
  sent_back: { fill: '#7F89A8', glyph: 'return', word: 'Sent back', fillClass: 'bg-[#7F89A8]', textClass: 'text-[#5B6584]' },
  locked: { fill: '#141A42', glyph: 'padlock', word: 'Restricted', fillClass: 'bg-[#141A42]', textClass: 'text-[#141A42]' },
}

export const STATUS_ICON_KINDS = Object.keys(STATUS_ICON) as StatusIconKind[]

/** The kind of a step row's status (`JourneyStep.status`). */
export function kindForStepStatus(status: string | null | undefined): StatusIconKind {
  switch (status) {
    case 'done':
      return 'done'
    case 'attention':
      return 'needs_you'
    case 'failed':
      return 'gap'
    case 'working':
      return 'working'
    case 'returned':
    case 'sent_back':
      return 'sent_back'
    default:
      return 'waiting'
  }
}

/** The kind of a compliance thread status (`met`, `not_applicable`, `look_at`, `pending`). */
export function kindForCompliance(status: string | null | undefined): StatusIconKind {
  switch (status) {
    case 'met':
      return 'done'
    case 'not_applicable':
      return 'not_applicable'
    case 'look_at':
      return 'needs_you'
    default:
      return 'waiting'
  }
}

/** The accessible name: the word the caller prints, else the kind's word. */
export function statusIconLabel(kind: StatusIconKind, label?: string | null): string {
  return (label ?? '').trim() || STATUS_ICON[kind].word
}
